import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import { HistoryPanel } from '@/components/documents';
import { SearchSelect, type SearchOption } from '@/components/SearchSelect';
import {
  ActionBar,
  ColumnToggle,
  DataGrid,
  DetailPane,
  SplitView,
  StatusBar,
  readStored,
  writeStored,
  type DesktopAction,
  type DetailTab,
  type GridColumn,
  type GridSort,
} from '@/components/desktop';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  Modal,
  SearchBar,
  Select,
  errorMessage,
  shortDate,
} from '@/components/ui';
import { formatDateTime } from '@/format';
import { useAuth } from '@/stores/auth';
import { confirmAction, confirmDiscard } from '@/stores/confirm';
import { toast } from '@/stores/toast';
import { describeDevice } from '../settings/device';
import { AccessLines } from './AccessLines';
import { UserPermissionsEditor } from './PermissionGrid';
import { FarmAssignment, useFarmsAndRoles, type Scope } from './shared';

type UserRow = components['schemas']['UserListItemDto'];
type RoleRow = components['schemas']['RoleListItemDto'];
type Form = 'create' | 'edit' | 'roles' | 'farms' | 'permissions' | 'bulkFarms';
type StatusFilter = '' | 'all' | 'off' | 'locked' | 'never' | 'idle' | 'temp';

const HIDDEN_KEY = 'poultry.grid.iam-users.hidden';
const IDLE_DAYS = 90;

/**
 * Users: who can sign in, with what roles, on which farms (.docs/019).
 *
 * Laid out like every other list in the app — the action bar works on the
 * chosen row, the details pane shows it, and forms open full-page. Every
 * change to someone the server would refuse is disabled here first, with the
 * server's own reason: you can change someone only if you can already do
 * everything they can, and never yourself.
 */
export function Users() {
  const { can, user: me } = useAuth();
  const queryClient = useQueryClient();
  const { roles, farms } = useFarmsAndRoles();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [farmFilter, setFarmFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [extrasOnly, setExtrasOnly] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<GridSort>({ key: 'name', direction: 'asc' });
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState<Form | null>(null);
  const [issued, setIssued] = useState<{ name: string; password: string; expiresAt: string } | null>(null);
  const [bulkResult, setBulkResult] = useState<components['schemas']['BulkResultDto'] | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(readStored<string[]>(HIDDEN_KEY, [])));

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['users'] });

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/api/v1/users'),
  });

  const act = useMutation({
    mutationFn: (v: { run: () => Promise<unknown>; done: string }) => v.run(),
    onSuccess: (_r, v) => {
      invalidate();
      toast.success(v.done);
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not change the account')),
  });

  const reset = useMutation({
    mutationFn: (row: UserRow) =>
      api.post<{ password: string; expiresAt: string }>(`/api/v1/users/${row.id}/reset-password`),
    onSuccess: (r, row) => {
      invalidate();
      setIssued({ name: row.fullName, password: r.password, expiresAt: r.expiresAt });
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not reset the password')),
  });

  const bulk = useMutation({
    mutationFn: (body: { action: string; roleCode?: string; scope?: Scope }) =>
      api.post<components['schemas']['BulkResultDto']>('/api/v1/users/bulk', { ids: [...checked], ...body }),
    onSuccess: (r) => {
      invalidate();
      setChecked(new Set());
      if (r.skipped.length > 0) setBulkResult(r);
      else toast.success(`Done for ${r.done} ${r.done === 1 ? 'person' : 'people'}.`);
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not make the change')),
  });

  const all = useMemo(() => data ?? [], [data]);
  const needle = search.trim().toLowerCase();
  // When the screen opened: "not seen for 90 days" need not tick by the second.
  const [now] = useState(() => Date.now());
  const rows = useMemo(() => {
    const filtered = all.filter((u) => {
      if (needle && !(u.userId.toLowerCase().includes(needle) || u.fullName.toLowerCase().includes(needle) || (u.mobile ?? '').includes(needle))) {
        return false;
      }
      if (roleFilter && !u.roles.includes(roleFilter)) return false;
      if (farmFilter && !u.allFarms && !u.farms.some((f) => f.id === farmFilter)) return false;
      if (extrasOnly && !u.hasExtras) return false;
      const seen = u.lastSeenAt ? new Date(u.lastSeenAt).getTime() : null;
      switch (statusFilter) {
        case '':
          return u.status === 'ACTIVE';
        case 'off':
          return u.status !== 'ACTIVE';
        case 'locked':
          return u.isLocked;
        case 'never':
          return u.lastLoginAt === null;
        case 'idle':
          return u.status === 'ACTIVE' && (seen === null || now - seen > IDLE_DAYS * 86_400_000);
        case 'temp':
          return u.mustChangePassword;
        default:
          return true;
      }
    });
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = (u: UserRow): string | number => {
      switch (sort.key) {
        case 'userId':
          return u.userId.toLowerCase();
        case 'roles':
          return u.roleNames.join(', ').toLowerCase();
        case 'last':
          return u.lastSeenAt ? new Date(u.lastSeenAt).getTime() : 0;
        case 'status':
          return u.status;
        default:
          return u.fullName.toLowerCase();
      }
    };
    return [...filtered].sort((a, b) => (key(a) < key(b) ? -dir : key(a) > key(b) ? dir : 0));
  }, [all, needle, roleFilter, farmFilter, extrasOnly, statusFilter, sort, now]);

  const selected = rows.find((u) => u.id === selectedKey) ?? null;

  function rowActions(row: UserRow): DesktopAction[] {
    const isSelf = me?.id === row.id;
    const canUpdate = can('iam.user.update');
    const blocked = !row.canManage;
    const why = row.cannotManageReason ?? '';
    const open = (f: Form) => () => {
      setSelectedKey(row.id);
      setForm(f);
    };
    const confirmThen = (title: string, message: string, confirmLabel: string, run: () => void, danger = false) => () => {
      setSelectedKey(row.id);
      void confirmAction({ title, message, confirmLabel, danger }).then((ok) => ok && run());
    };
    return [
      {
        label: 'Edit details',
        icon: 'edit',
        shortcut: 'Enter',
        onSelect: open('edit'),
        allowed: canUpdate,
        enabled: isSelf || !blocked,
        reason: why,
      },
      { label: 'Roles', icon: 'people', onSelect: open('roles'), allowed: canUpdate, enabled: !blocked, reason: why },
      { label: 'Farms', icon: 'allFarms', onSelect: open('farms'), allowed: canUpdate, enabled: !blocked, reason: why },
      { label: 'What they can do', icon: 'checklist', onSelect: open('permissions'), allowed: can('iam.role.read') },
      {
        label: 'Reset password',
        icon: 'locked',
        separatorBefore: true,
        allowed: canUpdate,
        enabled: !blocked,
        reason: isSelf ? 'Change your own password in Settings › Password & sign-in.' : why,
        onSelect: confirmThen(
          `Reset ${row.fullName}'s password?`,
          'Their current password stops working at once and they are signed out everywhere. You will be shown a temporary password to give them; they must choose their own when they sign in, and it stops working after three days.',
          'Reset password',
          () => reset.mutate(row),
        ),
      },
      ...(row.isLocked
        ? [
            {
              label: 'Unlock',
              icon: 'reactivate' as const,
              allowed: canUpdate,
              enabled: !blocked,
              reason: why,
              onSelect: confirmThen(
                `Unlock ${row.fullName}?`,
                'The wrong-password locks on this account are cleared, so they can sign in again now.',
                'Unlock',
                () => act.mutate({ run: () => api.post(`/api/v1/users/${row.id}/unlock`), done: `${row.fullName} can sign in again.` }),
              ),
            },
          ]
        : []),
      {
        label: 'Sign out everywhere',
        icon: 'leaver',
        allowed: canUpdate,
        enabled: !blocked,
        reason: why,
        onSelect: confirmThen(
          `Sign ${row.fullName} out everywhere?`,
          'Every computer and phone they are signed in on is signed out at once. Their password stays the same.',
          'Sign out everywhere',
          () =>
            act.mutate({
              run: () => api.post(`/api/v1/users/${row.id}/sessions/revoke-all`),
              done: `${row.fullName} is signed out everywhere.`,
            }),
        ),
      },
      ...(row.twoStepEnabled
        ? [
            {
              label: 'Reset two-step sign-in',
              icon: 'repeat' as const,
              allowed: canUpdate,
              enabled: !blocked,
              reason: why,
              onSelect: confirmThen(
                `Reset two-step sign-in for ${row.fullName}?`,
                'For a lost phone. Two-step is switched off and they are signed out everywhere; they can sign in with their password and switch it on again.',
                'Reset two-step',
                () =>
                  act.mutate({
                    run: () => api.post(`/api/v1/users/${row.id}/two-step/reset`),
                    done: `Two-step sign-in is off for ${row.fullName}.`,
                  }),
              ),
            },
          ]
        : []),
      row.status === 'ACTIVE'
        ? {
            label: 'Switch off',
            icon: 'suspend',
            danger: true,
            separatorBefore: true,
            allowed: canUpdate,
            enabled: !blocked,
            reason: isSelf ? 'You cannot switch off your own account.' : why,
            onSelect: confirmThen(
              `Switch off ${row.fullName}?`,
              'They are signed out everywhere at once and cannot sign in until someone switches them back on.',
              'Switch off',
              () =>
                act.mutate({
                  run: () => api.patch(`/api/v1/users/${row.id}/status`, { status: 'SUSPENDED' }),
                  done: `${row.fullName} is switched off and signed out everywhere.`,
                }),
              true,
            ),
          }
        : {
            label: 'Switch on',
            icon: 'reactivate',
            separatorBefore: true,
            allowed: canUpdate,
            enabled: !blocked,
            reason: why,
            onSelect: confirmThen(
              `Switch ${row.fullName} back on?`,
              'They can sign in again with their password.',
              'Switch on',
              () =>
                act.mutate({
                  run: () => api.patch(`/api/v1/users/${row.id}/status`, { status: 'ACTIVE' }),
                  done: `${row.fullName} can sign in again.`,
                }),
            ),
          },
      {
        label: 'Delete',
        icon: 'remove',
        danger: true,
        allowed: can('iam.user.delete'),
        enabled: !blocked && row.lastLoginAt === null,
        reason: blocked ? why : 'They have used the system, so they cannot be deleted. Switch them off instead.',
        onSelect: confirmThen(
          `Delete ${row.fullName}?`,
          'They have never signed in, so nothing they did is lost. This cannot be undone.',
          'Delete',
          () =>
            act.mutate({
              run: () => api.delete(`/api/v1/users/${row.id}`),
              done: `${row.fullName} deleted.`,
            }),
          true,
        ),
      },
    ];
  }

  // As on the Stock screen: a plain click ticks the one row it chose, so the
  // bar turns to "several people" only once two or more are ticked.
  const bulkMode = checked.size >= 2;
  const bulkActions: DesktopAction[] =
    bulkMode && can('iam.user.update')
      ? [
          {
            label: 'Switch off',
            icon: 'suspend',
            danger: true,
            onSelect: () =>
              void confirmAction({
                title: `Switch off ${checked.size} people?`,
                message: 'They are signed out everywhere at once. Anyone you cannot change is skipped and listed.',
                confirmLabel: 'Switch off',
                danger: true,
              }).then((ok) => ok && bulk.mutate({ action: 'suspend' })),
          },
          { label: 'Switch on', icon: 'reactivate', onSelect: () => bulk.mutate({ action: 'reactivate' }) },
          {
            label: 'Sign out everywhere',
            icon: 'leaver',
            onSelect: () =>
              void confirmAction({
                title: `Sign ${checked.size} people out everywhere?`,
                message: 'Every computer and phone they are signed in on is signed out at once.',
                confirmLabel: 'Sign out everywhere',
              }).then((ok) => ok && bulk.mutate({ action: 'signOut' })),
          },
          { label: 'Set farms', icon: 'allFarms', onSelect: () => setForm('bulkFarms') },
        ]
      : [];

  const toolbarActions: DesktopAction[] = [
    { label: 'Add user', icon: 'add', primary: true, onSelect: () => setForm('create'), allowed: can('iam.user.create') },
    ...(bulkMode
      ? bulkActions
      : selected
        ? rowActions(selected)
        : [{ label: 'Edit details', onSelect: () => {}, allowed: can('iam.user.update'), enabled: false, reason: 'Choose a user first' }]),
  ];

  const passwordDialog = issued && (
    <PasswordDialog name={issued.name} password={issued.password} expiresAt={issued.expiresAt} onClose={() => setIssued(null)} />
  );
  const bulkDialog = bulkResult && <BulkResultDialog result={bulkResult} onClose={() => setBulkResult(null)} />;

  // A form takes over the screen. An edit whose user has dropped out of view
  // (a search, a filter) simply closes.
  const activeForm = form && (form === 'create' || form === 'bulkFarms' || selected) ? form : null;
  if (activeForm) {
    const done = (message: string) => {
      invalidate();
      toast.success(message);
      setForm(null);
    };
    const close = () => setForm(null);
    return (
      <div className="workbench">
        <div className="workbench__main workbench__main--form">
          {activeForm === 'create' && (
            <CreateUserForm
              roles={roles}
              onCancel={close}
              onSaved={(created) => {
                done(`${created.name} added.`);
                setIssued(created);
              }}
            />
          )}
          {activeForm === 'edit' && selected && <EditUserForm key={selected.id} user={selected} onCancel={close} onSaved={done} />}
          {activeForm === 'roles' && selected && <RolesForm key={selected.id} user={selected} roles={roles} onCancel={close} onSaved={done} />}
          {activeForm === 'farms' && selected && <EditScopeForm key={selected.id} user={selected} roles={roles} onCancel={close} onSaved={done} />}
          {activeForm === 'bulkFarms' && (
            <BulkFarmsForm
              count={checked.size}
              onCancel={close}
              onSave={(scope) => {
                bulk.mutate({ action: 'setFarms', scope });
                setForm(null);
              }}
            />
          )}
          {activeForm === 'permissions' && selected && (
            <UserPermissionsEditor
              key={selected.id}
              userId={selected.id}
              userName={selected.fullName}
              version={selected.version}
              onCancel={close}
              onDone={() => done(`What ${selected.fullName} can do is updated. It takes effect on their next click.`)}
            />
          )}
        </div>
        {passwordDialog}
      </div>
    );
  }

  const columns: Array<GridColumn<UserRow>> = [
    { key: 'name', header: 'Name', width: 190, sortable: true, render: (u) => u.fullName },
    { key: 'userId', header: 'User ID', width: 150, sortable: true, render: (u) => <span className="mono mono--ascase">{u.userId}</span> },
    { key: 'roles', header: 'Roles', width: 190, sortable: true, render: (u) => u.roleNames.join(', ') || <span className="dim">None</span> },
    { key: 'farms', header: 'Farms', width: 200, render: (u) => <FarmsSummary user={u} /> },
    { key: 'twoStep', header: 'Two-step', width: 90, render: (u) => (u.twoStepEnabled ? 'On' : <span className="dim">Off</span>) },
    {
      key: 'last',
      header: 'Last seen',
      width: 120,
      sortable: true,
      render: (u) => <span className="dim">{u.lastSeenAt ? shortDate(u.lastSeenAt) : 'Never'}</span>,
    },
    { key: 'status', header: 'Status', width: 170, sortable: true, render: (u) => <UserStatus user={u} /> },
  ];
  const visibleColumns = columns.filter((c) => !hidden.has(c.key));

  const tabs: DetailTab[] = selected
    ? [
        { id: 'details', label: 'Details', icon: 'details', content: <UserDetails user={selected} /> },
        { id: 'access', label: 'What they can do', icon: 'checklist', content: <UserAccess user={selected} /> },
        { id: 'farms', label: 'Farms & stores', icon: 'allFarms', content: <ScopeDetails user={selected} /> },
        { id: 'signin', label: 'Sign-in', icon: 'locked', content: <SignInDetails user={selected} /> },
        { id: 'history', label: 'History', icon: 'history', content: <HistoryPanel path={`/api/v1/users/${selected.id}/history`} /> },
      ]
    : [];

  const off = all.filter((u) => u.status !== 'ACTIVE').length;
  const locked = all.filter((u) => u.isLocked).length;

  return (
    <div className="workbench">
      <SplitView
        id="iam-users"
        direction="column"
        initial={380}
        firstLabel="user list"
        first={
          <div className="workbench__main">
            <div className="filterbar">
              <SearchBar value={search} onChange={setSearch} placeholder="Search by name, user ID or mobile…" />
              <Select
                label="Role"
                value={roleFilter}
                onChange={setRoleFilter}
                placeholder="Any role"
                options={roles.map((r) => ({ value: r.code, label: r.nameEn }))}
              />
              <Select
                label="Farm"
                value={farmFilter}
                onChange={setFarmFilter}
                placeholder="Any farm"
                options={farms.map((f) => ({ value: f.id, label: `${f.nameEn} (${f.code})` }))}
              />
              <Select
                label="Show"
                value={statusFilter}
                onChange={(v) => setStatusFilter(v as StatusFilter)}
                placeholder="Switched on"
                options={[
                  { value: 'all', label: 'Everyone' },
                  { value: 'off', label: 'Switched off' },
                  { value: 'locked', label: 'Locked' },
                  { value: 'never', label: 'Never signed in' },
                  { value: 'idle', label: `Not seen for ${IDLE_DAYS} days` },
                  { value: 'temp', label: 'Must choose a password' },
                ]}
              />
              <label className="settings__toggle">
                <input type="checkbox" checked={extrasOnly} onChange={(e) => setExtrasOnly(e.target.checked)} />
                <span className="field__label">Has extra permissions</span>
              </label>
            </div>

            <ActionBar actions={toolbarActions}>
              {bulkMode && (
                <>
                  <span className="actionbar__count">{checked.size} chosen</span>
                  <BulkRoleControls
                    roles={roles}
                    onAdd={(roleCode) => bulk.mutate({ action: 'addRole', roleCode })}
                    onRemove={(roleCode) => bulk.mutate({ action: 'removeRole', roleCode })}
                  />
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => setChecked(new Set())}>
                    Clear
                  </button>
                </>
              )}
              {can('iam.user.export') && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  title="Everyone, their roles and farms — for an access review"
                  onClick={() =>
                    void api
                      .download('/api/v1/users/export', 'users.csv')
                      .catch((e: unknown) => toast.error(errorMessage(e, 'Could not download the list')))
                  }
                >
                  Download
                </button>
              )}
              <ColumnToggle
                columns={columns.map((c) => ({ key: c.key, label: c.header }))}
                hidden={hidden}
                onChange={(next) => {
                  setHidden(next);
                  writeStored(HIDDEN_KEY, [...next]);
                }}
              />
            </ActionBar>

            {isError && <ErrorNote message={error.message} />}

            <DataGrid
              id="iam-users"
              label="Users"
              columns={visibleColumns}
              rows={rows}
              rowKey={(u) => u.id}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              onOpen={(u) => {
                setSelectedKey(u.id);
                if (can('iam.user.update') && (u.canManage || u.id === me?.id)) setForm('edit');
              }}
              actions={rowActions}
              sort={sort}
              onSortChange={setSort}
              loading={isPending}
              empty={needle || roleFilter || farmFilter || statusFilter || extrasOnly ? 'Nobody matches.' : 'No users yet.'}
              {...(can('iam.user.update')
                ? {
                    selectedKeys: checked,
                    onToggleSelect: (key: string) =>
                      setChecked((prev) => {
                        const next = new Set(prev);
                        if (next.has(key)) next.delete(key);
                        else next.add(key);
                        return next;
                      }),
                    onToggleSelectAll: () =>
                      setChecked((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)))),
                    onSelectRange: (keys: string[]) => setChecked(new Set(keys)),
                  }
                : {})}
            />
          </div>
        }
        second={
          <DetailPane
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            empty="Choose a user to see their details."
            title={
              selected && (
                <span>
                  {selected.fullName} <span className="mono mono--ascase dim">{selected.userId}</span>
                </span>
              )
            }
          />
        }
      />
      <StatusBar>
        <span>
          {rows.length} of {all.length} users
        </span>
        {off > 0 && <span>{off} switched off</span>}
        {locked > 0 && <span>{locked} locked</span>}
        <span>{selected ? `Chosen: ${selected.fullName}` : 'No user chosen'}</span>
      </StatusBar>
      {passwordDialog}
      {bulkDialog}
    </div>
  );
}

function FarmsSummary({ user }: { user: UserRow }) {
  if (user.allFarms) return <span className="pill">All farms</span>;
  if (user.farms.length === 0) return <span className="pill pill--alert">⚠ None</span>;
  return (
    <>
      {user.farms.map((f) => (
        <span key={f.id} className="pill" title={f.name}>
          {f.name}
        </span>
      ))}
    </>
  );
}

function UserStatus({ user }: { user: UserRow }) {
  return (
    <>
      {user.status === 'ACTIVE' ? <span className="pill">Active</span> : <span className="pill pill--alert">⚠ Switched off</span>}
      {user.isLocked && <span className="pill pill--warn">Locked</span>}
      {user.mustChangePassword && <span className="pill">New password due</span>}
    </>
  );
}

function DetailList({ items }: { items: Array<[string, ReactNode]> }) {
  return (
    <dl className="detaillist">
      {items.map(([label, value]) => (
        <DetailItem key={label} label={label} value={value} />
      ))}
    </dl>
  );
}

function DetailItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

function UserDetails({ user }: { user: UserRow }) {
  return (
    <DetailList
      items={[
        ['Name', user.fullName],
        ['Name (Urdu)', user.fullNameUr ? <span key="ur" className="urdu">{user.fullNameUr}</span> : '—'],
        ['User ID', <span key="id" className="mono mono--ascase">{user.userId}</span>],
        ['Mobile', user.mobile ?? '—'],
        ['Roles', user.roleNames.join(', ') || 'None'],
        ['Staff record', user.staff ? `${user.staff.name} (${user.staff.code})` : 'Not linked — My Pay will not work for them'],
        ['Status', <UserStatus key="status" user={user} />],
        ...(user.canManage ? [] : [['Can you change them?', user.cannotManageReason ?? 'No'] as [string, ReactNode]]),
      ]}
    />
  );
}

/** Line by line, with why: from which role, or an extra given to them. */
function UserAccess({ user }: { user: UserRow }) {
  const access = useQuery({
    queryKey: ['users', user.id, 'access'],
    queryFn: () => api.get(`/api/v1/users/${user.id}/access` as '/api/v1/users/{id}/access'),
  });
  if (access.isPending) return <p className="muted">Loading…</p>;
  if (access.isError) return <ErrorNote message="Could not load what they can do." />;
  return (
    <div className="stack">
      <p>
        <strong>Roles:</strong> {access.data.roleNames.join(', ') || 'None'}
      </p>
      <AccessLines lines={access.data.lines} emptyText="They can do nothing yet." />
    </div>
  );
}

function ScopeDetails({ user }: { user: UserRow }) {
  const access = useQuery({
    queryKey: ['users', user.id, 'access'],
    queryFn: () => api.get(`/api/v1/users/${user.id}/access` as '/api/v1/users/{id}/access'),
  });
  return (
    <DetailList
      items={[
        ['Farms', access.data?.farms ?? (user.allFarms ? 'All farms' : user.farms.map((f) => f.name).join(', ') || 'None')],
        ['Stores', access.data?.stores ?? '…'],
        ['Note', "A farm's stores follow it, including stores added to it later."],
      ]}
    />
  );
}

/** Where they are signed in, recent sign-ins, two-step and password. */
function SignInDetails({ user }: { user: UserRow }) {
  const sessions = useQuery({
    queryKey: ['users', user.id, 'sessions'],
    queryFn: () => api.get(`/api/v1/users/${user.id}/sessions` as '/api/v1/users/{id}/sessions'),
    enabled: user.canManage,
  });
  const history = useQuery({
    queryKey: ['history', `/api/v1/users/${user.id}/history`],
    queryFn: () => api.get(`/api/v1/users/${user.id}/history` as '/api/v1/users/{id}/history'),
  });
  // The history route answers newest first, so the ten most recent sign-ins
  // are the first ten that match.
  const signIns = ((history.data ?? []) as unknown as Array<{ id: string; action: string; detail: string | null; createdAt: string }>)
    .filter((h) => h.action === 'LOGIN' || h.action === 'LOGIN_FAILED')
    .slice(0, 10);

  return (
    <div className="stack">
      <DetailList
        items={[
          ['Last signed in', user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never'],
          ['Last seen', user.lastSeenAt ? formatDateTime(user.lastSeenAt) : 'Never'],
          ['Two-step sign-in', user.twoStepEnabled ? 'On' : 'Off'],
          [
            'Password',
            user.mustChangePassword
              ? `Temporary — they must choose their own${user.tempPasswordExpiresAt ? `; it stops working ${formatDateTime(user.tempPasswordExpiresAt)}` : ''}`
              : 'Their own',
          ],
        ]}
      />
      {user.canManage && (
        <div>
          <h4>Signed in on</h4>
          {sessions.isPending && <p className="muted">Loading…</p>}
          {sessions.data && sessions.data.length === 0 && <p className="muted">Nowhere right now.</p>}
          {sessions.data && sessions.data.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Network address</th>
                  <th>Signed in</th>
                  <th>Last used</th>
                </tr>
              </thead>
              <tbody>
                {sessions.data.map((s) => (
                  <tr key={s.id}>
                    <td>{describeDevice(s.userAgent)}</td>
                    <td className="mono mono--ascase">{s.ipAddress ?? '—'}</td>
                    <td>{formatDateTime(s.createdAt)}</td>
                    <td>{formatDateTime(s.lastUsedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      <div>
        <h4>Recent sign-ins</h4>
        {signIns.length === 0 && <p className="muted">None recorded.</p>}
        {signIns.length > 0 && (
          <ul className="stacklist">
            {signIns.map((h) => (
              <li key={h.id}>
                {formatDateTime(h.createdAt)} — {h.action === 'LOGIN' ? 'Signed in' : 'Failed'}
                {h.detail ? <span className="dim"> · {h.detail}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Shown once after a password is made or reset. */
function PasswordDialog({
  name,
  password,
  expiresAt,
  onClose,
}: {
  name: string;
  password: string;
  expiresAt: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Modal title={`Temporary password for ${name}`} onClose={onClose}>
      <div className="stack">
        <h2>Temporary password for {name}</h2>
        <p>
          This is shown once. Give it to them now. Capital and small letters matter. They must choose their own
          password when they sign in, and this one stops working on {new Date(expiresAt).toLocaleDateString()}.
        </p>
        <p className="passwordreveal">{password}</p>
        <div className="formactions">
          <button type="button" className="btn" onClick={() => void copy()}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
          <span className="formactions__spacer" />
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

function BulkResultDialog({ result, onClose }: { result: components['schemas']['BulkResultDto']; onClose: () => void }) {
  return (
    <Modal title="Some people were skipped" onClose={onClose}>
      <div className="stack">
        <h2>Done for {result.done}. Skipped {result.skipped.length}:</h2>
        <ul className="stacklist">
          {result.skipped.map((s) => (
            <li key={s.id}>
              <strong>{s.name}</strong> — {s.reason}
            </li>
          ))}
        </ul>
        <div className="formactions">
          <span className="formactions__spacer" />
          <button type="button" className="btn btn--primary" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </Modal>
  );
}

function BulkRoleControls({
  roles,
  onAdd,
  onRemove,
}: {
  roles: RoleRow[];
  onAdd: (roleCode: string) => void;
  onRemove: (roleCode: string) => void;
}) {
  const [roleCode, setRoleCode] = useState('');
  const role = roles.find((r) => r.code === roleCode);
  return (
    <span className="bulkroles">
      <select
        className="field__input field__input--sm"
        aria-label="Role to add or remove"
        value={roleCode}
        onChange={(e) => setRoleCode(e.target.value)}
      >
        <option value="">Role…</option>
        {roles.map((r) => (
          <option key={r.code} value={r.code}>
            {r.nameEn}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        disabled={!role}
        onClick={() =>
          role &&
          void confirmAction({
            title: `Give ${role.nameEn} to everyone chosen?`,
            message: 'Anyone you cannot change is skipped and listed.',
            confirmLabel: 'Give role',
          }).then((ok) => ok && onAdd(role.code))
        }
      >
        Add role
      </button>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        disabled={!role}
        onClick={() =>
          role &&
          void confirmAction({
            title: `Take ${role.nameEn} from everyone chosen?`,
            message: 'Anyone for whom it is their only role is skipped and listed.',
            confirmLabel: 'Take role away',
            danger: true,
          }).then((ok) => ok && onRemove(role.code))
        }
      >
        Remove role
      </button>
    </span>
  );
}

/** Ticks one or more roles, each with what it is for. */
function RolePicker({
  roles,
  value,
  onChange,
}: {
  roles: RoleRow[];
  value: string[];
  onChange: (codes: string[]) => void;
}) {
  return (
    <fieldset className="rolepicker">
      <legend className="field__label">Roles</legend>
      {roles.map((r) => {
        const on = value.includes(r.code);
        return (
          <label key={r.code} className={`rolepicker__item ${on ? 'rolepicker__item--on' : ''}`}>
            <input
              type="checkbox"
              checked={on}
              onChange={(e) =>
                onChange(e.target.checked ? [...value, r.code] : value.filter((c) => c !== r.code))
              }
            />
            <span>
              <strong>{r.nameEn}</strong>
              <span className="dim"> · {r.permissionCount} things</span>
              {r.description && <span className="rolepicker__about">{r.description}</span>}
            </span>
          </label>
        );
      })}
      <p className="settings__hint">
        {value.length === 0
          ? 'Choose at least one role.'
          : `They will be able to do everything ${value.length === 1 ? 'this role allows' : 'these roles allow, together'}.`}
      </p>
    </fieldset>
  );
}

function CreateUserForm({
  roles,
  onCancel,
  onSaved,
}: {
  roles: RoleRow[];
  onCancel: () => void;
  onSaved: (created: { name: string; password: string; expiresAt: string }) => void;
}) {
  const [form, setForm] = useState({ userId: '', fullName: '', fullNameUr: '', mobile: '', employeeId: '', staffLabel: '' });
  const [roleCodes, setRoleCodes] = useState<string[]>([]);
  const [scope, setScope] = useState<Scope>({ allFarms: false, farmIds: [] });
  const [idCheck, setIdCheck] = useState<{ available: boolean; reason: string | null } | null>(null);
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const dirty = Object.values(form).some(Boolean) || roleCodes.length > 0 || scope.allFarms || scope.farmIds.length > 0;

  // Is the user ID free? Asked a moment after typing stops.
  useEffect(() => {
    const id = form.userId.trim();
    if (!id) return;
    const timer = setTimeout(() => {
      api
        .get('/api/v1/users/check-id', { userId: id })
        .then(setIdCheck)
        .catch(() => setIdCheck(null));
    }, 350);
    return () => clearTimeout(timer);
  }, [form.userId]);

  const save = useMutation({
    mutationFn: () =>
      api.post<{ id: string; userId: string; password: string; expiresAt: string }>('/api/v1/users', {
        userId: form.userId.trim(),
        fullName: form.fullName.trim(),
        ...(form.fullNameUr.trim() ? { fullNameUr: form.fullNameUr.trim() } : {}),
        ...(form.mobile.trim() ? { mobile: form.mobile.trim() } : {}),
        ...(form.employeeId ? { employeeId: form.employeeId } : {}),
        roleCodes,
        allFarms: scope.allFarms,
        farmIds: scope.farmIds,
      }),
    onSuccess: (r) => onSaved({ name: form.fullName.trim(), password: r.password, expiresAt: r.expiresAt }),
  });

  // A check for an ID since cleared says nothing.
  const shownCheck = form.userId.trim() ? idCheck : null;
  const problems = [
    !form.fullName.trim() && 'Type their name.',
    !form.userId.trim() && 'Choose a user ID.',
    shownCheck && !shownCheck.available && shownCheck.reason,
    roleCodes.length === 0 && 'Choose at least one role.',
    !scope.allFarms && scope.farmIds.length === 0 && 'Choose their farms, or All farms.',
  ].filter(Boolean) as string[];

  const companyWide = roles.some((r) => roleCodes.includes(r.code) && r.companyWide);

  async function staffSearch(query: string): Promise<SearchOption[]> {
    const rows = await api.get('/api/v1/users/staff-records', { search: query });
    return rows.map((r) => ({ value: r.id, label: `${r.nameEn} (${r.code})`, meta: r.farmName ?? 'Head office' }));
  }

  async function cancel() {
    if (dirty && !(await confirmDiscard())) return;
    onCancel();
  }

  return (
    <FormPanel
      title="Add a user"
      intro="Their roles decide what they can do; their farms decide where. A temporary password is made for them, and they choose their own when they first sign in."
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (problems.length === 0) save.mutate();
        }}
      >
        <FormGroup title="Person" columns={3} card>
          <div className="field field--full">
            <SearchSelect
              label="Staff record"
              value={form.employeeId}
              selectedLabel={form.staffLabel}
              search={staffSearch}
              placeholder="Search staff by name or code…"
              hint="Optional. Links them for My Pay, and fills in their name."
              onChange={(o) =>
                set(
                  o
                    ? {
                        employeeId: o.value,
                        staffLabel: o.label,
                        fullName: form.fullName || o.label.replace(/\s\([^)]*\)$/, ''),
                      }
                    : { employeeId: '', staffLabel: '' },
                )
              }
            />
          </div>
          <Field label="Full name" required value={form.fullName} onChange={(fullName) => set({ fullName })} />
          <Field label="Name (Urdu)" urdu value={form.fullNameUr} onChange={(fullNameUr) => set({ fullNameUr })} />
          <Field label="Mobile" value={form.mobile} onChange={(mobile) => set({ mobile })} placeholder="0300-1234567" hint="They can sign in with this too" />
        </FormGroup>

        <FormGroup title="Sign-in" columns={3} card>
          <Field
            label="User ID"
            required
            value={form.userId}
            onChange={(userId) => set({ userId })}
            placeholder="tariq.mahmood"
            hint={
              shownCheck
                ? shownCheck.available
                  ? '✓ Available. What they type to sign in; cannot be changed later.'
                  : `⚠ ${shownCheck.reason}`
                : 'What they type to sign in. Letters, numbers, dots and dashes; at least one letter. Cannot be changed later.'
            }
          />
        </FormGroup>

        <FormGroup title="Roles" card>
          <RolePicker roles={roles} value={roleCodes} onChange={setRoleCodes} />
        </FormGroup>

        <FormGroup title="Farms" card>
          <FarmAssignment allFarms={scope.allFarms} farmIds={scope.farmIds} onChange={setScope} suggestAll={companyWide} />
        </FormGroup>

        {problems.length > 0 && form.userId && <p className="settings__hint">Still needed: {problems.join(' ')}</p>}
        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not add the user')} />}

        <FormActions onCancel={() => void cancel()}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending || problems.length > 0}>
            {save.isPending ? 'Saving…' : 'Add user'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}

function EditUserForm({ user, onCancel, onSaved }: { user: UserRow; onCancel: () => void; onSaved: (message: string) => void }) {
  const [form, setForm] = useState({
    fullName: user.fullName,
    fullNameUr: user.fullNameUr ?? '',
    mobile: user.mobile ?? '',
    employeeId: user.staff?.id ?? '',
    staffLabel: user.staff ? `${user.staff.name} (${user.staff.code})` : '',
  });
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/v1/users/${user.id}`, {
        fullName: form.fullName.trim(),
        fullNameUr: form.fullNameUr.trim(),
        mobile: form.mobile.trim(),
        ...(form.employeeId !== (user.staff?.id ?? '') ? { employeeId: form.employeeId } : {}),
      }),
    onSuccess: () => onSaved(`${form.fullName.trim()} updated.`),
  });

  async function staffSearch(query: string): Promise<SearchOption[]> {
    const rows = await api.get('/api/v1/users/staff-records', { search: query });
    return rows.map((r) => ({ value: r.id, label: `${r.nameEn} (${r.code})`, meta: r.farmName ?? 'Head office' }));
  }

  return (
    <FormPanel title={`Edit ${user.fullName}`}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormGroup title="Person" columns={3} card>
          <Field label="User ID" value={user.userId} onChange={() => {}} disabled hint="Cannot be changed" />
          <Field label="Full name" required value={form.fullName} onChange={(fullName) => set({ fullName })} />
          <Field label="Name (Urdu)" urdu value={form.fullNameUr} onChange={(fullNameUr) => set({ fullNameUr })} />
          <Field
            label="Mobile"
            value={form.mobile}
            onChange={(mobile) => set({ mobile })}
            placeholder="0300-1234567"
            hint="Leave empty to remove it"
          />
          <div className="field field--full">
            <SearchSelect
              label="Staff record"
              value={form.employeeId}
              selectedLabel={form.staffLabel}
              search={staffSearch}
              placeholder="Search staff by name or code…"
              hint={
                form.staffLabel && !form.staffLabel.startsWith(form.fullName.trim())
                  ? '⚠ The staff record has a different name. Check it is the same person.'
                  : 'Links them for My Pay. Clear it to unlink.'
              }
              onChange={(o) => set(o ? { employeeId: o.value, staffLabel: o.label } : { employeeId: '', staffLabel: '' })}
            />
          </div>
        </FormGroup>

        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

        <FormActions onCancel={onCancel}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}

function RolesForm({
  user,
  roles,
  onCancel,
  onSaved,
}: {
  user: UserRow;
  roles: RoleRow[];
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const [roleCodes, setRoleCodes] = useState<string[]>(user.roles);
  const changed =
    roleCodes.length !== user.roles.length || roleCodes.some((c) => !user.roles.includes(c));
  const names = (codes: string[]) => roles.filter((r) => codes.includes(r.code)).map((r) => r.nameEn);
  const added = names(roleCodes.filter((c) => !user.roles.includes(c)));
  const removed = names(user.roles.filter((c) => !roleCodes.includes(c)));

  const save = useMutation({
    mutationFn: () => api.patch(`/api/v1/users/${user.id}/roles`, { roleCodes, version: user.version }),
    onSuccess: () => onSaved(`${user.fullName}'s roles are changed. It takes effect on their next click.`),
  });

  async function submit() {
    const ok = await confirmAction({
      title: `Change ${user.fullName}'s roles?`,
      message: [
        added.length ? `Give: ${added.join(', ')}.` : '',
        removed.length ? `Take away: ${removed.join(', ')}.` : '',
        'It takes effect on their next click.',
      ]
        .filter(Boolean)
        .join(' '),
      confirmLabel: 'Change roles',
    });
    if (ok) save.mutate();
  }

  return (
    <FormPanel title={`Roles for ${user.fullName}`} intro={`Currently: ${user.roleNames.join(', ') || 'no role'}.`}>
      <div className="stack">
        <FormGroup title="Roles" card>
          <RolePicker roles={roles} value={roleCodes} onChange={setRoleCodes} />
        </FormGroup>

        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not change the roles')} />}

        <FormActions onCancel={onCancel}>
          <button
            type="button"
            className="btn btn--primary"
            disabled={save.isPending || !changed || roleCodes.length === 0}
            onClick={() => void submit()}
          >
            {save.isPending ? 'Saving…' : 'Change roles'}
          </button>
        </FormActions>
      </div>
    </FormPanel>
  );
}

function EditScopeForm({
  user,
  roles,
  onCancel,
  onSaved,
}: {
  user: UserRow;
  roles: RoleRow[];
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const [scope, setScope] = useState<Scope>({ allFarms: user.allFarms, farmIds: user.farms.map((f) => f.id) });
  const companyWide = roles.some((r) => user.roles.includes(r.code) && r.companyWide);

  const save = useMutation({
    mutationFn: () => api.patch(`/api/v1/users/${user.id}/scope`, { ...scope, version: user.version }),
    onSuccess: () => onSaved(`Farms for ${user.fullName} updated. It takes effect on their next click.`),
  });

  return (
    <FormPanel title={`Farms for ${user.fullName}`}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormGroup title="Farms" card>
          <FarmAssignment allFarms={scope.allFarms} farmIds={scope.farmIds} onChange={setScope} suggestAll={companyWide} />
        </FormGroup>

        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

        <FormActions onCancel={onCancel}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save farms'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}

function BulkFarmsForm({ count, onCancel, onSave }: { count: number; onCancel: () => void; onSave: (scope: Scope) => void }) {
  const [scope, setScope] = useState<Scope>({ allFarms: false, farmIds: [] });
  return (
    <FormPanel
      title={`Farms for ${count} ${count === 1 ? 'person' : 'people'}`}
      intro="This replaces the farms of everyone chosen. Anyone you cannot change is skipped and listed."
    >
      <div className="stack">
        <FormGroup title="Farms" card>
          <FarmAssignment allFarms={scope.allFarms} farmIds={scope.farmIds} onChange={setScope} />
        </FormGroup>
        <FormActions onCancel={onCancel}>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!scope.allFarms && scope.farmIds.length === 0}
            onClick={() => onSave(scope)}
          >
            Set farms
          </button>
        </FormActions>
      </div>
    </FormPanel>
  );
}
